import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.personApiEnv;

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

export interface IndividualPerson {
    uuid: string;
    name: string;
    socialId: string;
    email: string;
    mobile: string;
}

export interface LegalPerson {
    personId: string;
    legalName: string;
    govTaxId: string;
}

export interface Signatory {
    individualUuid: string;
    fullName: string;
    socialId: string;
    email: string;
    mobile: string;
    isActive: boolean;
    allowedOperations: Array<{ operation: string; representationFormat: string } | string>;
    signAsEntries: { signAs: string; useDigitalCertificate: boolean }[];
}

export interface PersonAddress {
    id: number;
    zipcode: string | null;
    streetType: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    district: string | null;
    cityId: number | null;
}

export interface PersonSearchResult {
    uuid: string;                    // person aggregate id (→ billing external_id)
    personType: 'INDIVIDUAL' | 'LEGAL_PERSON';
    name: string;
    alias: string | null;
    contacts: { type: string; content: string }[];
    documents: { type: string; number: string }[];
    profiles?: string[];
}

export interface LegalPersonSearchResult {
    personId: string;
    legalName: string;
    fantasyName: string;
    govTaxId: string;
    govTaxIdFormatted: string;
}

export const personApi = {
    searchLegalPersons: async (query: string, signal?: AbortSignal): Promise<LegalPersonSearchResult[]> => {
        const stripped = query.replace(/[.\-/]/g, '').toUpperCase();
        const hasConsecutiveDigits = /\d{2,}/.test(stripped);
        const hasNoSpaces = !/\s/.test(stripped);
        const isCnpjSearch = hasConsecutiveDigits && hasNoSpaces && stripped.length <= 14;
        const queryParam = isCnpjSearch
            ? `govTaxId=${encodeURIComponent(stripped)}`
            : `legalName=${encodeURIComponent(query)}`;
        const res = await fetch(
            `${API.address}/legal-persons/page-list?${queryParam}&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders(), signal }
        );
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            const detail = errData?.message || `status ${res.status}`;
            throw new Error(`Erro ao buscar cedente: ${detail}`);
        }
        const data = await res.json();
        return (data.list || []).slice(0, 6);
    },

    searchByNameOrCpf: async (query: string): Promise<IndividualPerson[]> => {
        const clean = query.trim();
        if (!clean) return [];
        const isNumeric = /^\d+$/.test(clean.replace(/\D/g, '')) && clean.replace(/\D/g, '').length >= 3;
        const param = isNumeric ? `name=${clean.replace(/\D/g, '')}` : `name=${encodeURIComponent(clean)}`;
        const res = await fetch(
            `${API.address}/individual/page-list?${param}&email=&mobile=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return (data.list || []).filter((p: any) => p.email);
    },

    // Busca consolidada por CPF ou CNPJ (endpoint /person/all/<doc> — vw_person_search,
    // detecta documento por cpf/cnpj; a variante /search/all busca só por nome).
    // O path duplica "/person": a base (API.address) já é .../person e o serviço person
    // define as rotas como /person/all/... com strip_prefixes=["/person"].
    searchByDocument: async (doc: string): Promise<PersonSearchResult | null> => {
        const clean = doc.replace(/\D/g, '');
        if (clean.length !== 11 && clean.length !== 14) return null;
        const res = await fetch(`${API.address}/person/all/${clean}`, { headers: await getHeaders() });
        if (!res.ok) return null;
        const data = await res.json();
        const list: PersonSearchResult[] = data.results || [];
        return list.find(p => p.documents?.some(d => (d.number || '').replace(/\D/g, '') === clean)) || list[0] || null;
    },

    // Endereços de um person (PF ou PJ). Mesmo padrão de path duplicado "/person".
    getAddresses: async (personId: string): Promise<PersonAddress[]> => {
        const res = await fetch(`${API.address}/person/${personId}/addresses`, { headers: await getHeaders() });
        if (!res.ok) return [];
        const data = await res.json();
        return data.addresses || [];
    },

    searchIndividualByCpf: async (cpf: string): Promise<IndividualPerson | null> => {
        const clean = cpf.replace(/\D/g, '');
        if (clean.length !== 11) return null;
        const res = await fetch(
            `${API.address}/individual/page-list?name=${clean}&email=&mobile=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const list: any[] = data.list || [];
        return list.find((p: any) => p.socialId?.replace(/\D/g, '') === clean) || null;
    },

    searchLegalPersonByCnpj: async (cnpj: string): Promise<LegalPerson | null> => {
        const clean = cnpj.replace(/\D/g, '');
        if (clean.length !== 14) return null;
        const res = await fetch(
            `${API.address}/legal-persons/page-list?govTaxId=${encodeURIComponent(clean)}&legalName=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const list: any[] = data.list || [];
        return list.find((p: any) => p.govTaxId?.replace(/\D/g, '') === clean) || null;
    },

    getSignatories: async (legalPersonUuid: string): Promise<Signatory[]> => {
        const res = await fetch(
            `${API.address}/legal-persons/${legalPersonUuid}/signatories`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.signatories || [];
    },
};
